import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  DollarSign,
  BarChart3,
  Settings,
  Menu,
  X,
  Shield,
  FileText,
  Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getAdminUser, hasAdminPermission } from '@/admin/services/adminAuthService';

interface SidebarItem {
  name: string;
  href: string;
  icon: React.ElementType;
  permission?: string;
}

interface AdminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const adminUser = getAdminUser();

  const sidebarItems: SidebarItem[] = [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: 'Customer Earnings',
      href: '/admin/customers',
      icon: DollarSign,
      permission: 'earnings:read',
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      permission: 'analytics:read',
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      permission: 'users:read',
    },
    {
      name: 'Audit Logs',
      href: '/admin/audit',
      icon: FileText,
      permission: 'audit:read',
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Settings,
    },
  ];

  // Filter items based on permissions
  const visibleItems = sidebarItems.filter(item => 
    !item.permission || hasAdminPermission(item.permission)
  );

  return (
    <>
      {/* Mobile overlay */}
      {!collapsed && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-30 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                Admin Portal
              </span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn("", collapsed && "mx-auto")}
          >
            {collapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
          </Button>
        </div>

        {/* User Info */}
        {!collapsed && adminUser && (
          <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="text-sm">
              <p className="font-medium text-gray-900 dark:text-white truncate">
                {adminUser.email}
              </p>
              <p className="text-gray-500 dark:text-gray-400 capitalize">
                {adminUser.role.replace('_', ' ')}
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-2">
          <TooltipProvider>
            {visibleItems.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;

              const linkContent = (
                <Link
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700",
                    collapsed && "justify-center"
                  )}
                >
                  <Icon className={cn("h-5 w-5 flex-shrink-0", collapsed && "h-6 w-6")} />
                  {!collapsed && <span>{item.name}</span>}
                </Link>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>
                      {linkContent}
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.name}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return <div key={item.href}>{linkContent}</div>;
            })}
          </TooltipProvider>
        </nav>

        {/* Status Indicator */}
        <div className={cn(
          "border-t border-gray-200 dark:border-gray-700 p-4",
          collapsed && "px-2"
        )}>
          <div className="flex items-center gap-2">
            <Activity className={cn(
              "h-4 w-4 text-green-500",
              collapsed && "mx-auto"
            )} />
            {!collapsed && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                System Operational
              </span>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;